import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostBinding, Input, OnDestroy, inject } from '@angular/core';
import { environment } from '@environment';

@Component({
  selector: 'app-ue-finance-banner',
  templateUrl: './ue-finance-banner.component.html',
  styleUrls: ['./ue-finance-banner.component.scss'],
  standalone: false,
})
export class UeFinanceBannerComponent implements AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly hostRef = inject(ElementRef<HTMLElement>);

  public readonly showBanner = environment.showUEFinanceBanner;

  // Optional per-page desktop size override; falls back to the SCSS default.
  @Input()
  @HostBinding('style.--ue-banner-desktop-width')
  public desktopWidth?: string;

  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private updateTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onWindowResize = () => this.scheduleUpdate();

  public ngAfterViewInit(): void {
    if (!this.showBanner) {
      return;
    }

    const body = this.document.body;
    const view = this.document.defaultView;

    // The cookie bar (fixed) and blue column are outside this component and appear/resize
    // dynamically, so watch the document for both DOM and size changes.
    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver(() => this.scheduleUpdate());
      this.mutationObserver.observe(body, { childList: true, subtree: true });
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this.resizeObserver.observe(body);
    }

    view?.addEventListener('resize', this.onWindowResize);
    this.updateLayout();
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.document.defaultView?.removeEventListener('resize', this.onWindowResize);
    if (this.updateTimer != null) {
      clearTimeout(this.updateTimer);
      this.updateTimer = null;
    }
  }

  private scheduleUpdate(): void {
    if (this.updateTimer != null) {
      return;
    }

    this.updateTimer = setTimeout(() => {
      this.updateTimer = null;
      this.updateLayout();
    }, 50);
  }

  private updateLayout(): void {
    const host = this.hostRef.nativeElement;

    const cookieBar = this.document.querySelector<HTMLElement>('.cookie_banner');
    host.style.setProperty('--ue-cookie-bar-height', `${cookieBar?.offsetHeight ?? 0}px`);

    const blueColumn = this.document.querySelector<HTMLElement>('.page-container .column.left');
    if (blueColumn && blueColumn.offsetWidth > 0) {
      const rect = blueColumn.getBoundingClientRect();
      host.style.setProperty('--ue-blue-center', `${rect.left + rect.width / 2}px`);
      host.style.setProperty('--ue-blue-width', `${rect.width}px`);
    } else {
      host.style.removeProperty('--ue-blue-center');
      host.style.removeProperty('--ue-blue-width');
    }
  }
}
